import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import isequal from 'lodash.isequal';
import {createSelector} from 'reselect';
import type {ReduxState} from '~redux/index';

import {CLASS_EMPTY, CLASS_OK, LABEL_ID_SEPARATOR, UNKNOWN_MODEL_ID} from '~constants/constants';
import {theme} from '~constants/theme';
import {byOldestClassificationOrAnnotationFirst, getUUID} from '~utils/miscUtils';
import {RESET} from '~redux/types';
import {
  AnnotationEventType,
  Coordinate,
  ImageData,
  ImageLabelPayloadByModel,
  LabelItem,
  LabelSource,
  LabelType,
} from '~redux/types/images';
import {Condition, InspectionSubType, InspectionType, Model} from '~redux/types/models';
import {PartialWithRequired} from '~redux/types/utils';
import {
  addAnnotationItem,
  createAnnotationItem,
  deleteAllKeypoints,
  deleteAnnotationItem,
  getModelIdForAnnotationItem,
  isSimilarityLabel,
  offsetCoordinates,
  updateAnnotationItem,
} from '~redux/reducers/annotatorReducer.utils';

import {
  GroupedAnnotationItems,
  groupPolygonItemsByProximity,
} from '~components/images/annotator/utils/groupLabelChips.utils';

interface CopiedPolygonData {
  imageId: string;
  polygon: PolygonItem;
}

// The InteractionMode defines the allowed user interactions with polygons on the canvas
export type InteractionMode = 'enabled' | 'disabled' | 'drawing';

export interface AnnotationItem {
  id: string;
  labelId: string;
  color: LabelItem['colorCode'];
  alias: LabelItem['alias'];
  // modelId can be undefined for unknown machine prediction labels
  modelId: string | undefined;
  modelVersion?: string;
  inspectionType?: InspectionType;
  inspectionSubType?: InspectionSubType;
  type: LabelType;
  isMachinePrediction: boolean;
  isUnknownMachinePrediction?: boolean;
  isCurationMachinePrediction?: boolean;
  userEmail: string;
  updatedAt: number; // Note: this is a unix timestamp in seconds (!) not milliseconds
  changeType: 'none' | 'new' | 'modified' | 'deleted';
  coordinates?: Coordinate[];
  probability?: number;
  area?: number;
  hiddenForSmartSession?: boolean;
  isDefaultPrediction?: boolean;
  condition?: string;
  generatedByPolygonService?: boolean;
  /**
   * Tool that produced this annotation, sent to the backend as telemetry. Only set for labels
   * created in the current editing session — labels loaded from the backend carry no tool, and
   * re-saving them must not be counted as a new tool use.
   */
  annotationEventType?: AnnotationEventType;
  source?: LabelSource;
  // Set for evaluation predictions flagged as unstable by enhanced testing.
  unstable?: boolean;
}

export interface PolygonItem extends AnnotationItem {
  type: 'polygon';
  coordinates: Coordinate[];
}

export interface ClassificationItem extends AnnotationItem {
  type: 'class';
}

export interface KeypointItem extends AnnotationItem {
  type: 'keypoint';
  coordinates: Coordinate[];
}

export interface PendingKeypoint {
  classId: string;
  coordinates: Coordinate;
  userEmail: string;
}

export interface PendingPolygon extends Partial<Omit<PolygonItem, 'changeType' | 'isMachinePrediction'>> {
  coordinates: PolygonItem['coordinates'];
}

export interface AnnotationItemsByModel {
  [modelId: string]: AnnotationItem[];
}

/**
 * A single entry in the per-session annotation save history. Each auto-save creates one step
 * holding the models that were changed (used to undo via the backend) and the saved label payload
 * (used to redo by re-saving the snapshot).
 */
export interface AnnotationHistoryStep {
  changedModelIds: string[];
  payloadByModel: ImageLabelPayloadByModel;
}

export interface AnnotatorReduxState {
  currentModel: Model | undefined;
  isImageReadOnly: boolean;
  isDrawingMode: boolean;
  isFullscreen: boolean;
  showSidebar: boolean;
  showHumanAnnotations: boolean;
  showKeypointConnections: boolean;
  showDistanceThreshold: boolean;
  showMachinePredictions: boolean;
  showKeypoints: boolean;
  showSimilarityPredictions: boolean;
  showRegionOfInterest: boolean;
  showLabelChips: boolean;
  showCoordinatesIndicator: boolean;
  annotationItems: AnnotationItemsByModel | null;
  models: Model[];
  activePolygonId: string | null;
  hoveredPolygonId: string | null;
  draggedPolygonId: string | null;
  draggedKeypointId: string | null;
  activeKeypointModelId: string | null;
  hoveredKeypoint: {modelId: string; type: 'human' | 'machine'; keypointId: string; keypointLabelId: string} | null;
  polygonContextMenu: {
    polygonId: string | null;
    position: Coordinate;
    imagePosition?: Coordinate;
  } | null;
  pendingPolygon: Pick<PolygonItem, 'coordinates' | 'generatedByPolygonService'> | null;
  copiedPolygonData: CopiedPolygonData | null;
  unsavedChangesDialog: {
    isOpen: boolean;
    newImageIndex?: number;
  };
  isKeypointMode: boolean;
  pendingKeypoints: PendingKeypoint[] | null;
  keypointContextMenu: {
    modelId?: string | null;
    position: Coordinate;
    imagePosition?: Coordinate;
  } | null;
  notification: {open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning'} | null;
  isMeasuringDistance: boolean;
  measurementPoints: {start: Coordinate | null; end: Coordinate | null};
  measurementContextMenu: {position: Coordinate} | null;
  isLookingForCoordinates: boolean;
  isSmartSelectMode: boolean;
  undoStack: AnnotationHistoryStep[];
  redoStack: AnnotationHistoryStep[];
  lastSavedAt: number | null;
  isSaving: boolean;
  failedSavePayload: ImageLabelPayloadByModel | null;
}

export const initialState: AnnotatorReduxState = {
  currentModel: undefined,
  isImageReadOnly: false,
  isDrawingMode: false,
  isFullscreen: false,
  showSidebar: false,
  showMachinePredictions: true,
  showKeypoints: true,
  showSimilarityPredictions: true,
  showRegionOfInterest: false,
  showHumanAnnotations: true,
  showKeypointConnections: true,
  showDistanceThreshold: true,
  showLabelChips: true,
  showCoordinatesIndicator: true,
  annotationItems: {},
  models: [],
  activePolygonId: null,
  hoveredPolygonId: null,
  draggedPolygonId: null,
  draggedKeypointId: null,
  activeKeypointModelId: null,
  hoveredKeypoint: null,
  polygonContextMenu: null,
  pendingPolygon: null,
  copiedPolygonData: null,
  unsavedChangesDialog: {
    isOpen: false,
  },
  isKeypointMode: false,
  pendingKeypoints: null,
  keypointContextMenu: null,
  notification: null,
  isMeasuringDistance: false,
  measurementPoints: {start: null, end: null},
  measurementContextMenu: null,
  isLookingForCoordinates: false,
  isSmartSelectMode: false,
  undoStack: [],
  redoStack: [],
  lastSavedAt: null,
  isSaving: false,
  failedSavePayload: null,
};

const name = 'annotator';

const annotatorSlice = createSlice({
  name,
  initialState,
  reducers: {
    /**
     * Takes an image and transforms all of its latest annotation labels to AnnotationItems which is an
     * internal data structure used by the annotator. The AnnotationItems are used to render the state of the annotator
     * and keep track of changes to the annotations.
     */
    setAnnotationItems(
      state,
      action: PayloadAction<{image: ImageData | null; labelItems: LabelItem[]; models: Model[]}>,
    ) {
      const {image, labelItems, models} = action.payload;
      state.annotationItems = {};
      state.models = models || []; // <-- store models in state
      if (!image) {
        return;
      }

      Object.entries(image.annotations).forEach(([modelId, latestAnnotations]) => {
        const {latestHumanAnnotation, machinePrediction} = latestAnnotations;

        if (!latestHumanAnnotation && !machinePrediction) {
          return;
        }

        state.annotationItems![modelId] = [];

        latestHumanAnnotation?.labels.forEach((annotationLabel) => {
          state.annotationItems![modelId].push(
            createAnnotationItem({
              isMachinePrediction: false,
              annotationLabel,
              labelItems,
              models,
              imageDataAnnotation: latestHumanAnnotation,
            }),
          );
        });

        machinePrediction?.labels.forEach((annotationLabel) => {
          state.annotationItems![modelId].push(
            createAnnotationItem({
              isMachinePrediction: true,
              annotationLabel,
              labelItems,
              models,
              imageDataAnnotation: machinePrediction,
            }),
          );
        });
      });
    },
    resetAnnotationItems(state) {
      state.annotationItems = null;
    },
    updatePolygonItem(state, action: PayloadAction<PartialWithRequired<AnnotationItem, 'modelId' | 'id'>>) {
      const itemToUpdate = action.payload;
      const {modelId} = itemToUpdate;

      if (!state.annotationItems || !modelId || !state.annotationItems[modelId]) {
        return;
      }

      state.annotationItems[modelId] = updateAnnotationItem(itemToUpdate, state.annotationItems[modelId]);
    },
    updateKeypointItem(state, action: PayloadAction<PartialWithRequired<KeypointItem, 'modelId' | 'id'>>) {
      const itemToUpdate = action.payload;
      const {modelId} = itemToUpdate;

      if (!state.annotationItems || !modelId || !state.annotationItems[modelId]) {
        return;
      }

      state.annotationItems[modelId] = updateAnnotationItem(itemToUpdate, state.annotationItems[modelId]);
    },
    deleteAnnotationItem(state, action: PayloadAction<AnnotationItem['id']>) {
      const annotationItemId = action.payload;
      if (!state.annotationItems) {
        return;
      }

      const modelId = getModelIdForAnnotationItem(annotationItemId, state.annotationItems);
      if (!modelId) {
        return;
      }

      state.annotationItems[modelId] = deleteAnnotationItem(annotationItemId, state.annotationItems[modelId]);
    },
    addPolygonItemFromPendingPolygon(
      state,
      action: PayloadAction<{labelItem: LabelItem; userEmail: string; modelId: string}>,
    ) {
      const {modelId, labelItem, userEmail} = action.payload;

      if (!state.pendingPolygon) {
        return;
      }
      if (!state.annotationItems) {
        state.annotationItems = {};
      }
      if (!state.annotationItems[modelId]) {
        state.annotationItems[modelId] = [];
      }

      // create and add new polygon item
      const {id, colorCode: color, alias} = labelItem;
      const itemToAdd: PolygonItem = {
        ...state.pendingPolygon,
        id: getUUID(),
        labelId: id,
        alias,
        color,
        userEmail,
        modelId,
        updatedAt: Date.now() / 1000,
        type: 'polygon',
        changeType: 'new',
        isMachinePrediction: false,
        source: 'human',
        annotationEventType: state.pendingPolygon.generatedByPolygonService ? 'smart_lasso' : 'manual',
      };
      state.annotationItems[modelId] = addAnnotationItem(itemToAdd, state.annotationItems[modelId]);

      // reset pending state
      state.pendingPolygon = null;
      state.isDrawingMode = false;
      state.isSmartSelectMode = false;
    },
    addClassificationItem(state, action: PayloadAction<{labelItem: LabelItem; userEmail: string; modelId: string}>) {
      const {labelItem, userEmail, modelId} = action.payload;

      if (!state.annotationItems) {
        state.annotationItems = {};
      }

      if (!state.annotationItems[modelId]) {
        state.annotationItems[modelId] = [];
      }

      // create and add new classification item
      // Note: classification items are always considered "new", never "modified" since they are not editable
      // in the same way as polygons. They are always re-assigned by adding a new class.
      const {id, colorCode, alias} = labelItem;
      const itemToAdd: ClassificationItem = {
        id: getUUID(),
        color: colorCode,
        alias,
        labelId: id,
        userEmail,
        modelId,
        inspectionType: undefined,
        updatedAt: Date.now() / 1000,
        type: 'class',
        changeType: 'new',
        isMachinePrediction: false,
        source: 'human',
        annotationEventType: 'manual',
      };
      state.annotationItems[modelId] = addAnnotationItem(itemToAdd, state.annotationItems[modelId]);
    },
    updatePolygonItemLabel(
      state,
      action: PayloadAction<{
        polygonItem: PolygonItem;
        newLabel: LabelItem;
        previousModelId: string;
        newModelId: string;
      }>,
    ) {
      const {polygonItem, newLabel, previousModelId, newModelId} = action.payload;

      if (!state.annotationItems) {
        state.annotationItems = {};
      }

      if (previousModelId === newModelId) {
        // if the new label is from the same model, only update the labelId, alias, color and date
        const updatedItem = {
          id: polygonItem.id,
          labelId: newLabel.id,
          alias: newLabel.alias,
          color: newLabel.colorCode,
        };
        state.annotationItems[previousModelId] = updateAnnotationItem(
          updatedItem,
          state.annotationItems[previousModelId],
        );
      } else {
        // if the new label is from a different model, we need to:
        // 1. create a new annotation item using the existing item's data and the new label
        const newItem: AnnotationItem = {
          ...polygonItem,
          labelId: newLabel.id,
          alias: newLabel.alias,
          color: newLabel.colorCode,
          updatedAt: Date.now(),
          modelId: newModelId,
          changeType: 'new',
          // A polygon drawn in this session keeps its creation tool; a saved polygon reassigned to
          // another model is a move (the backend mints a new polygon id for it there).
          annotationEventType: polygonItem.changeType === 'new' ? polygonItem.annotationEventType : 'move_polygon',
        };
        // 2. add the new annotation item to the new model
        state.annotationItems[newModelId] = addAnnotationItem(newItem, state.annotationItems[newModelId]);
        // 3. delete the existing annotation item from the previous model
        state.annotationItems[previousModelId] = deleteAnnotationItem(
          polygonItem.id,
          state.annotationItems[previousModelId],
        );
      }
    },
    setPendingPolygon(state, action: PayloadAction<AnnotatorReduxState['pendingPolygon']>) {
      state.pendingPolygon = action.payload;
    },
    resetPendingPolygon(state) {
      state.pendingPolygon = null;
    },
    setActivePolygonId(state, action: PayloadAction<AnnotatorReduxState['activePolygonId']>) {
      state.activePolygonId = action.payload;
    },
    resetActivePolygonId(state) {
      state.activePolygonId = null;
    },
    toggleSidebar(state) {
      state.showSidebar = !state.showSidebar;
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.showSidebar = action.payload;
    },
    setFullscreen(state, action: PayloadAction<boolean>) {
      state.isFullscreen = action.payload;
    },
    toggleFullscreen(state) {
      state.isFullscreen = !state.isFullscreen;
    },
    setHoveredPolygonId(state, action: PayloadAction<AnnotatorReduxState['hoveredPolygonId']>) {
      state.hoveredPolygonId = action.payload;
    },
    resetHoveredPolygonId(state) {
      state.hoveredPolygonId = null;
    },
    setDraggedPolygonId(state, action: PayloadAction<AnnotatorReduxState['draggedPolygonId']>) {
      state.draggedPolygonId = action.payload;
    },
    resetDraggedPolygonId(state) {
      state.draggedPolygonId = null;
    },
    setDraggedKeypointId(state, action: PayloadAction<AnnotatorReduxState['draggedKeypointId']>) {
      state.draggedKeypointId = action.payload;
    },
    resetDraggedKeypointId(state) {
      state.draggedKeypointId = null;
    },
    setActiveKeypointModelId(state, action: PayloadAction<AnnotatorReduxState['activeKeypointModelId']>) {
      state.activeKeypointModelId = action.payload;
    },
    resetActiveKeypointModelId(state) {
      state.activeKeypointModelId = null;
    },
    showPolygonContextMenu(state, action: PayloadAction<NonNullable<AnnotatorReduxState['polygonContextMenu']>>) {
      state.polygonContextMenu = action.payload;
    },
    hidePolygonContextMenu(state) {
      state.polygonContextMenu = null;
    },
    setMachinePredictionsVisible(state, action: PayloadAction<AnnotatorReduxState['showMachinePredictions']>) {
      state.showMachinePredictions = action.payload;
    },
    setSimilarityPredictionsVisible(state, action: PayloadAction<AnnotatorReduxState['showSimilarityPredictions']>) {
      state.showSimilarityPredictions = action.payload;
    },
    setLabelChipsVisible(state, action: PayloadAction<AnnotatorReduxState['showLabelChips']>) {
      state.showLabelChips = action.payload;
    },
    setHumanAnnotationsVisible(state, action: PayloadAction<AnnotatorReduxState['showHumanAnnotations']>) {
      state.showHumanAnnotations = action.payload;
    },
    setKeypointConnectionsVisible(state, action: PayloadAction<AnnotatorReduxState['showKeypointConnections']>) {
      state.showKeypointConnections = action.payload;
    },
    setDistanceThresholdVisible(state, action: PayloadAction<AnnotatorReduxState['showDistanceThreshold']>) {
      state.showDistanceThreshold = action.payload;
    },
    setRegionOfInterestVisible(state, action: PayloadAction<AnnotatorReduxState['showRegionOfInterest']>) {
      state.showRegionOfInterest = action.payload;
    },
    setImageReadOnly(state, action: PayloadAction<AnnotatorReduxState['isImageReadOnly']>) {
      state.isImageReadOnly = action.payload;
    },
    setIsDrawingMode(state, action: PayloadAction<AnnotatorReduxState['isDrawingMode']>) {
      state.isDrawingMode = action.payload;
      state.activePolygonId = null;
      // Disable smart select mode when enabling drawing mode
      if (action.payload) {
        state.isSmartSelectMode = false;
      }
      if (!action.payload) {
        state.pendingPolygon = null;
      }
    },
    setSmartSelectMode(state, action: PayloadAction<AnnotatorReduxState['isSmartSelectMode']>) {
      state.isSmartSelectMode = action.payload;
      state.activePolygonId = null;
      // Disable other modes when enabling smart select mode
      if (action.payload) {
        state.isDrawingMode = false;
        state.isKeypointMode = false;
      }
      if (!action.payload) {
        state.pendingPolygon = null;
      }
    },
    copyPolygon(state, action: PayloadAction<AnnotatorReduxState['copiedPolygonData']>) {
      state.copiedPolygonData = action.payload;
    },
    pastePolygon(state, action: PayloadAction<{imageId: string}>) {
      // Disallow copy & pasting polygons from other models,
      // but allow when no model is selected
      if (
        (state.currentModel?.id && state.copiedPolygonData?.polygon.modelId !== state.currentModel.id) ||
        !state.copiedPolygonData
      ) {
        return;
      }

      if (!state.annotationItems) {
        state.annotationItems = {};
      }

      const {imageId, polygon} = state.copiedPolygonData;
      const modelId = polygon.modelId;

      if (!modelId) {
        return;
      }

      if (!state.annotationItems[modelId]) {
        state.annotationItems[modelId] = [];
      }

      const newPolygonItem: PolygonItem = {
        ...polygon,
        id: getUUID(),
        changeType: 'new',
        coordinates:
          imageId == action.payload.imageId ? offsetCoordinates(polygon.coordinates, 5, 5) : polygon.coordinates,
        updatedAt: Date.now() / 1000,
        annotationEventType: 'copy_polygon',
      };

      state.annotationItems[modelId] = addAnnotationItem(newPolygonItem, state.annotationItems[modelId]);
    },
    setCurrentModel(state, action: PayloadAction<Model | undefined>) {
      state.currentModel = action.payload;
    },
    showUnsavedChangesDialog(state, action: PayloadAction<{newImageIndex: number}>) {
      state.unsavedChangesDialog = {
        isOpen: true,
        newImageIndex: action.payload.newImageIndex,
      };
    },
    closeUnsavedChangesDialog(state) {
      state.unsavedChangesDialog = {
        isOpen: false,
      };
    },
    /**
     * Resets the active annotation, pending changes, interactive state (hovered, active, dragged polygon)
     * and disables drawing mode. Used when the user navigates to another image and there are unsaved changes.
     */
    discardActiveAnnotation(state) {
      state.annotationItems = null;
      state.isDrawingMode = false;
      state.pendingPolygon = null;
      state.hoveredPolygonId = null;
      state.activePolygonId = null;
      state.draggedPolygonId = null;
      state.draggedKeypointId = null;
    },
    discardPendingKeypoints(state) {
      state.isKeypointMode = false;
      state.pendingKeypoints = null;
    },
    /**
     * Resets all state except the current model and the copied polygon data. Used when leaving the annotator.
     */
    resetAllViewState(state) {
      return {
        ...initialState,
        // Keep the currentModel since it's managed by the model context
        currentModel: state.currentModel,
        // Keep the copied polygon so that it can be pasted at a later point
        copiedPolygonData: state.copiedPolygonData,
      };
    },
    setKeypointMode: (state, action: PayloadAction<boolean>) => {
      state.isKeypointMode = action.payload;
      // Disable smart select mode when enabling keypoint mode
      if (action.payload) {
        state.isSmartSelectMode = false;
      }
    },
    setPendingKeypoints: (
      state,
      action: PayloadAction<{
        keypoints: PendingKeypoint[];
      }>,
    ) => {
      state.pendingKeypoints = action.payload.keypoints;
    },
    deleteAllKeypoints(state, action: PayloadAction<{modelId: string}>) {
      const {modelId} = action.payload;

      if (!state.annotationItems || !state.annotationItems[modelId]) {
        return;
      }

      state.annotationItems[modelId] = deleteAllKeypoints(state.annotationItems[modelId]);

      state.pendingKeypoints = null;
    },
    showKeypointContextMenu: (
      state,
      action: PayloadAction<NonNullable<AnnotatorReduxState['keypointContextMenu']>>,
    ) => {
      state.keypointContextMenu = action.payload;
    },
    hideKeypointContextMenu: (state) => {
      state.keypointContextMenu = null;
    },
    setHoveredKeypoint(
      state,
      action: PayloadAction<{modelId: string; type: 'human' | 'machine'; keypointId: string; keypointLabelId: string}>,
    ) {
      state.hoveredKeypoint = action.payload;
    },
    resetHoveredKeypoint(state) {
      state.hoveredKeypoint = null;
    },
    toggleShowCoordinatesIndicator(state) {
      state.showCoordinatesIndicator = !state.showCoordinatesIndicator;
    },
    showNotification(
      state,
      action: PayloadAction<{message: string; severity: 'success' | 'error' | 'info' | 'warning'}>,
    ) {
      state.notification = {...action.payload, open: true};
    },
    hideNotification(state) {
      if (state.notification) {
        state.notification.open = false;
      }
    },
    startMeasurementMode(state) {
      state.isMeasuringDistance = true;
      state.measurementPoints = {start: null, end: null}; // Clear previous measurement
    },
    setMeasurementPoint(state, action: PayloadAction<Coordinate>) {
      if (!state.isMeasuringDistance) return;
      if (!state.measurementPoints.start) {
        state.measurementPoints.start = action.payload;
      } else if (!state.measurementPoints.end) {
        state.measurementPoints.end = action.payload;
        state.isMeasuringDistance = false; // End measurement mode after second point is set
      }
    },
    clearMeasurement(state) {
      state.measurementPoints = {start: null, end: null};
      state.isMeasuringDistance = false; // Ensure measurement mode is off
      state.measurementContextMenu = null; // Hide context menu if open
    },
    showMeasurementContextMenu(state, action: PayloadAction<{position: Coordinate}>) {
      state.measurementContextMenu = action.payload;
    },
    hideMeasurementContextMenu(state) {
      state.measurementContextMenu = null;
    },
    startCoordinateLookupMode(state) {
      state.isLookingForCoordinates = true;
    },
    stopCoordinateLookupMode(state) {
      state.isLookingForCoordinates = false;
    },
    copyCoordinatesAtPosition(state, action: PayloadAction<Coordinate>) {
      const {x, y} = action.payload;
      const coordsString = `x: ${Math.round(x)}\n y: ${Math.round(y)}`;
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(coordsString);
      }
      state.isLookingForCoordinates = false;
    },
    setIsSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    markAnnotationItemsSaved(state, action: PayloadAction<string[]>) {
      if (!state.annotationItems) {
        return;
      }
      for (const modelId of action.payload) {
        const items = state.annotationItems[modelId];
        if (!items) {
          continue;
        }
        state.annotationItems[modelId] = items
          .filter((item) => item.isMachinePrediction || item.changeType !== 'deleted')
          .map((item): AnnotationItem => (item.changeType === 'none' ? item : {...item, changeType: 'none'}));
      }
    },
    recordSaveFailure(state, action: PayloadAction<ImageLabelPayloadByModel>) {
      state.failedSavePayload = action.payload;
    },
    /**
     * Records a successful auto-save: pushes the step onto the undo stack, clears the redo stack
     * (a new change invalidates any redoable history) and stores the time of the save.
     */
    recordSave(state, action: PayloadAction<{step: AnnotationHistoryStep; savedAt: number}>) {
      state.undoStack.push(action.payload.step);
      state.redoStack = [];
      state.lastSavedAt = action.payload.savedAt;
      state.failedSavePayload = null;
    },
    /**
     * Records a successful undo: moves the most recent step from the undo stack to the redo stack.
     */
    recordUndo(state, action: PayloadAction<{savedAt: number}>) {
      const step = state.undoStack.pop();
      if (step) {
        state.redoStack.push(step);
      }
      state.lastSavedAt = action.payload.savedAt;
    },
    /**
     * Records a successful redo: moves the most recently undone step from the redo stack back to the undo stack.
     */
    recordRedo(state, action: PayloadAction<{savedAt: number}>) {
      const step = state.redoStack.pop();
      if (step) {
        state.undoStack.push(step);
      }
      state.lastSavedAt = action.payload.savedAt;
    },
    recordPartialUndo(state, action: PayloadAction<{undoneModelIds: string[]; savedAt: number}>) {
      const step = state.undoStack[state.undoStack.length - 1];
      if (!step) {
        return;
      }
      const {undoneModelIds, savedAt} = action.payload;
      const undonePayloadByModel: ImageLabelPayloadByModel = {};
      for (const modelId of undoneModelIds) {
        if (step.payloadByModel[modelId]) {
          undonePayloadByModel[modelId] = step.payloadByModel[modelId];
          delete step.payloadByModel[modelId];
        }
      }
      step.changedModelIds = step.changedModelIds.filter((modelId) => !undoneModelIds.includes(modelId));
      state.redoStack.push({changedModelIds: undoneModelIds, payloadByModel: undonePayloadByModel});
      if (step.changedModelIds.length === 0) {
        state.undoStack.pop();
      }
      state.lastSavedAt = savedAt;
    },
    resetAnnotationHistory(state) {
      state.undoStack = [];
      state.redoStack = [];
      state.lastSavedAt = null;
      state.isSaving = false;
      state.failedSavePayload = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(RESET, () => initialState);
  },
});

export const annotatorActions = annotatorSlice.actions;
export default annotatorSlice;

const select = (state: ReduxState): AnnotatorReduxState => state[name];

export const selectShowMachinePredictions = createSelector(select, (state) => state.showMachinePredictions);
export const selectShowKeypoints = createSelector(select, (state) => state.showKeypoints);
export const selectShowSimilarityPredictions = createSelector(select, (state) => state.showSimilarityPredictions);
export const selectShowRegionOfInterest = createSelector(select, (state) => state.showRegionOfInterest);
export const selectShowHumanAnnotations = createSelector(select, (state) => state.showHumanAnnotations);
export const selectShowKeypointConnections = createSelector(select, (state) => state.showKeypointConnections);
export const selectShowDistanceThreshold = createSelector(select, (state) => state.showDistanceThreshold);
export const selectShowLabelChips = createSelector(select, (state) => state.showLabelChips);
export const selectIsImageReadOnly = createSelector(select, (state) => state.isImageReadOnly);
export const selectIsDrawingMode = createSelector(select, (state) => state.isDrawingMode);
export const selectIsSmartSelectMode = createSelector(select, (state) => state.isSmartSelectMode);
export const selectIsSidebarOpen = createSelector(select, (state) => state.showSidebar);
export const selectIsFullscreen = createSelector(select, (state) => state.isFullscreen);
export const selectCopiedPolygonData = createSelector(select, (state) => state.copiedPolygonData);
export const selectCurrentModel = createSelector(select, (state) => state.currentModel);
export const selectPendingPolygon = createSelector(select, (state) => state.pendingPolygon);
export const selectCurrentModelId = createSelector(selectCurrentModel, (model) => model?.id);
export const selectHasPendingPolygon = createSelector(selectPendingPolygon, (pendingPolygon) => !!pendingPolygon);
export const selectPolygonContextMenu = createSelector(select, (state) => state.polygonContextMenu);
export const selectActivePolygonId = createSelector(select, (state) => state.activePolygonId);
export const selectHoveredPolygonId = createSelector(select, (state) => state.hoveredPolygonId);
export const selectActiveKeypointModelId = createSelector(select, (state) => state.activeKeypointModelId);
export const selectUnsavedChangesDialog = createSelector(select, (state) => state.unsavedChangesDialog);
export const selectAnnotationItems = createSelector(select, (state) => state.annotationItems);
export const selectAnnotatorModels = createSelector(select, (state) => state.models);
export const selectUndoStack = createSelector(select, (state) => state.undoStack);
export const selectRedoStack = createSelector(select, (state) => state.redoStack);
export const selectCanUndo = createSelector(selectUndoStack, (undoStack) => undoStack.length > 0);
export const selectCanRedo = createSelector(selectRedoStack, (redoStack) => redoStack.length > 0);
export const selectLastSavedAt = createSelector(select, (state) => state.lastSavedAt);
export const selectIsSaving = createSelector(select, (state) => state.isSaving);
export const selectFailedSavePayload = createSelector(select, (state) => state.failedSavePayload);

export const selectAllAnnotations = createSelector(
  [selectAnnotationItems],
  (annotationItems): AnnotationItem[] => {
    return Object.values(annotationItems ?? {}).flat();
  },
  {
    memoizeOptions: {
      // caches up to 10 different results, this is mostly relevant for the models that may change frequently in the archive.
      // TODO when reselect 5.0 is released, use weakMapMemoize instead (https://github.com/reduxjs/reselect#weakmapmemoizefunc---since-500)
      maxSize: 10,
    },
  },
);

export const selectActiveAnnotation = createSelector(
  [selectAnnotationItems, selectCurrentModelId],
  (annotationItems, currentModelId): AnnotationItem[] => {
    if (!currentModelId) {
      // aggregate all annotation items from all models
      return Object.values(annotationItems ?? {}).flat();
    }

    // Unknown machine prediction items are always shown, regardless of the current model
    const unknownMachinePredictionItems = annotationItems?.[UNKNOWN_MODEL_ID] ?? [];
    const currentAnnotationItems = annotationItems?.[currentModelId] ?? [];
    return [...unknownMachinePredictionItems, ...currentAnnotationItems];
  },
  {
    memoizeOptions: {
      // caches up to 10 different results, this is mostly relevant for the models that may change frequently in the archive.
      // TODO when reselect 5.0 is released, use weakMapMemoize instead (https://github.com/reduxjs/reselect#weakmapmemoizefunc---since-500)
      maxSize: 10,
    },
  },
);

export const selectDraggedPolygonId = createSelector(select, (state) => state.draggedPolygonId);
export const selectDraggedKeypointId = createSelector(select, (state) => state.draggedKeypointId);

export const selectActivePolygons = createSelector([selectAllAnnotations], (activeAnnotation): PolygonItem[] => {
  return activeAnnotation.filter(
    (label): label is PolygonItem =>
      label?.type === 'polygon' && label?.changeType !== 'deleted' && !label.hiddenForSmartSession,
  );
});

export const selectActiveKeypoints = createSelector([selectAllAnnotations], (activeAnnotation) => {
  return activeAnnotation.filter(
    (label): label is KeypointItem =>
      label?.type === 'keypoint' && label?.changeType !== 'deleted' && !label.hiddenForSmartSession,
  );
});

export const selectActiveClassifications = createSelector(
  [selectActiveAnnotation],
  (activeAnnotation): ClassificationItem[] => {
    return activeAnnotation.filter(
      (label): label is ClassificationItem =>
        label?.type === 'class' && label?.changeType !== 'deleted' && !label.hiddenForSmartSession,
    );
  },
);

export const selectActiveHumanPolygons = createSelector([selectActivePolygons], (polygonItems): PolygonItem[] => {
  return polygonItems.filter((polygon) => !polygon?.isMachinePrediction && !isSimilarityLabel(polygon.labelId));
});

export const selectActiveHumanKeypoints = createSelector(
  [selectActiveKeypoints, selectShowHumanAnnotations],
  (keypointItems, showHumanAnnotations): KeypointItem[] => {
    const humanKeypoints = keypointItems.filter((keypoint) => !keypoint?.isMachinePrediction);
    return showHumanAnnotations ? humanKeypoints : [];
  },
);

export const selectActiveSimilarityPredictionsPolygons = createSelector(
  [selectActivePolygons],
  (polygonItems): PolygonItem[] => {
    return polygonItems.filter((polygon) => isSimilarityLabel(polygon.labelId));
  },
);

export const selectActiveMachinePolygons = createSelector([selectActivePolygons], (polygonItems): PolygonItem[] => {
  return polygonItems.filter((polygon) => polygon?.isMachinePrediction && !isSimilarityLabel(polygon.labelId));
});

export const selectActiveMachineKeypoints = createSelector(
  [selectActiveKeypoints, selectShowMachinePredictions],
  (keypointItems, showMachinePredictions): KeypointItem[] => {
    const machineKeypoints = keypointItems.filter((keypoint) => keypoint?.isMachinePrediction);
    return showMachinePredictions ? machineKeypoints : [];
  },
);

export const selectActiveHumanClassifications = createSelector(
  [selectActiveClassifications, selectAnnotatorModels, selectCurrentModel],
  (classificationItems, models, currentModel) => {
    return classificationItems.filter(
      (classification) =>
        !classification?.isMachinePrediction && !isModelArchived(models, classification.modelId, currentModel),
    );
  },
);

export const selectActiveMachineClassifications = createSelector(
  [selectActiveClassifications, selectActiveMachinePolygons, selectAnnotatorModels, selectCurrentModel],
  (classificationItems, machinePolygons, models, currentModel) => {
    let machinePredictions = classificationItems.filter((classification) => classification?.isMachinePrediction);

    const isOkConditionPrediction = (classification: ClassificationItem) =>
      classification.condition === Condition.OK ||
      classification.condition === Condition.OTHER ||
      classification.isDefaultPrediction === true;

    const okEmptyClassifications = machinePredictions.filter(
      (classification) =>
        isOkConditionPrediction(classification) && !isModelArchived(models, classification.modelId, currentModel),
    );
    const nonOkEmptyClassifications = machinePredictions.filter(
      (classification) =>
        !isOkConditionPrediction(classification) && !isModelArchived(models, classification.modelId, currentModel),
    );

    // Combine all ok predictions with the same labelId prefix (OK/EMPTY) into one classification item, and afterwards create
    // an updated array of classification items that includes the combined items and the non-ok predictions
    const groupedOkPredictions: {[groupId: string]: ClassificationItem[]} = {};
    // a) group all ok predictions by labelId prefix
    okEmptyClassifications.forEach((prediction) => {
      // remove the unique model ID suffix to get the label (OK/EMPTY)
      const groupId = prediction.condition === Condition.OK ? 'OK' : prediction.labelId.split(LABEL_ID_SEPARATOR)[0];
      if (!groupedOkPredictions[groupId]) {
        groupedOkPredictions[groupId] = [];
      }
      groupedOkPredictions[groupId].push(prediction);
    });

    // b) Create a new classification item for each group of ok predictions
    let consolidatedOkPredictions = Object.entries(groupedOkPredictions).map(([groupId, group]): ClassificationItem => {
      const latestUpdatedAt = Math.max(...group.map((item) => item.updatedAt));
      const firstItem = group[0];

      let color: string;
      if (group.length === 1) {
        color = firstItem.color;
      } else if (groupId.startsWith(CLASS_EMPTY)) {
        color = theme.palette.maddox.classEmpty;
      } else if (groupId.startsWith(CLASS_OK)) {
        color = theme.palette.maddox.classOk;
      } else {
        color = theme.palette.maddox.unlabeled;
      }

      return {
        ...firstItem,
        labelId: group.length === 1 ? firstItem.labelId : groupId,
        alias: group.length === 1 ? firstItem.alias : groupId,
        color,
        updatedAt: latestUpdatedAt,
      };
    });

    // Ensure that NOK class predictions have priority over OK class predictions for defect type models
    const defectTypeClassifications = nonOkEmptyClassifications.filter(
      (item) =>
        item.inspectionType === InspectionType.surfaceInspection ||
        item.inspectionType === InspectionType.anomalyDetection ||
        (item.inspectionType === InspectionType.classification &&
          item.inspectionSubType === InspectionSubType.defect) ||
        item.inspectionType === InspectionType.legacyModel,
    );
    if (defectTypeClassifications.length > 0) {
      consolidatedOkPredictions = consolidatedOkPredictions.filter(
        (prediction) =>
          prediction.inspectionType !== InspectionType.surfaceInspection &&
          prediction.inspectionType !== InspectionType.anomalyDetection &&
          (prediction.inspectionType !== InspectionType.classification ||
            prediction.inspectionSubType !== InspectionSubType.defect) &&
          prediction.inspectionType !== InspectionType.legacyModel,
      );
    }

    const hasRealNonOkClassification = nonOkEmptyClassifications.length > 0;
    const hasRealMachineDefectPolygon = machinePolygons.some((polygon) => polygon.condition === Condition.NOK);
    if (hasRealNonOkClassification || hasRealMachineDefectPolygon) {
      consolidatedOkPredictions = consolidatedOkPredictions.filter(
        (prediction) => prediction.isDefaultPrediction !== true,
      );
    }

    // Ensure that polygons have priority over ok predictions
    // a) remove all defect detection and legacy model classifications when there are defect detection or legacy model polygons
    const defectDetectionPolygons = machinePolygons.filter(
      (item) =>
        item.inspectionType === InspectionType.surfaceInspection || item.inspectionType === InspectionType.legacyModel,
    );
    if (defectDetectionPolygons.length > 0) {
      consolidatedOkPredictions = consolidatedOkPredictions.filter(
        (prediction) =>
          prediction.inspectionType !== InspectionType.surfaceInspection &&
          prediction.inspectionType !== InspectionType.legacyModel,
      );
    }

    // b) remove all object detection classifications when there are object detection polygons
    const objectDetectionPolygons = machinePolygons.filter(
      (item) => item.inspectionType === InspectionType.objectDetection,
    );
    if (objectDetectionPolygons.length > 0) {
      consolidatedOkPredictions = consolidatedOkPredictions.filter(
        (prediction) => prediction.inspectionType !== InspectionType.objectDetection,
      );
    }

    machinePredictions = [...consolidatedOkPredictions, ...nonOkEmptyClassifications];
    return machinePredictions.sort((a, b) => {
      // first criteria: labels with id starting with "OK" should appear after ids starting with "EMPTY" (or others)
      // this is important for the label chips, where the OK label should be the last one
      // which also determines the image outline color
      const aIsOk = a.labelId.startsWith(CLASS_OK);
      const bIsOk = b.labelId.startsWith(CLASS_OK);
      if (aIsOk && !bIsOk) {
        return 1;
      } else if (!aIsOk && bIsOk) {
        return -1;
      } else {
        // second criteria: sort by updatedAt
        return byOldestClassificationOrAnnotationFirst(a, b);
      }
    });
  },
);

/**
 * @returns An array of the visible classifications in the following order:
 *
 * 1. Model predictions (oldest first, 0 or more, only added if model predictions are set to be visible)
 * 2. Human annotations (oldest first, 0 or more, only added if human annotations are set to be visible)
 *
 * The last element of the array describes the classification that is currently applied to the image border, as
 * it is the newest human classification.
 *
 * If there is no data, an empty array is returned.
 */
export const selectVisibleClassifications = createSelector(
  [
    selectActiveHumanClassifications,
    selectActiveMachineClassifications,
    selectShowMachinePredictions,
    selectShowHumanAnnotations,
  ],
  (humanClassifications, machineClassifications, showMachinePredictions, showHumanAnnotations) => {
    const classifications: ClassificationItem[] = [];
    if (showMachinePredictions && machineClassifications) {
      classifications.push(...machineClassifications);
    }
    if (showHumanAnnotations && humanClassifications) {
      classifications.push(...humanClassifications);
    }

    return classifications;
  },
);

/**
 * @returns The classification that is visually applied to the image. This takes the last annotation out of
 * the list of all visible classifications, see the order of classifications defined in {@link selectVisibleClassifications}.
 */
export const selectVisuallyAppliedClassification = createSelector(
  [selectVisibleClassifications],
  (visibleClassifications) => {
    return visibleClassifications.at(-1);
  },
);

export const selectGroupedPolygonLabelChips = createSelector(
  [selectActivePolygons],
  (polygonItems): GroupedAnnotationItems => {
    return groupPolygonItemsByProximity(polygonItems);
  },
);

export const selectSavableAnnotationItems = createSelector([selectAnnotationItems], (annotationItems) => {
  if (!annotationItems) {
    return [];
  }

  return Object.values(annotationItems)
    .flat()
    .filter((item) => item.changeType !== 'none');
});

export const selectPendingKeypoints = createSelector(select, (state) => state.pendingKeypoints);

export const selectHasSavableAnnotations = createSelector(
  [selectSavableAnnotationItems, selectPendingKeypoints],
  (annotationItems, pendingKeypoints): boolean => {
    return annotationItems.length > 0 || Boolean(pendingKeypoints?.length);
  },
);

export const selectIsInProgressInteraction = createSelector(
  [
    selectIsDrawingMode,
    selectIsSmartSelectMode,
    selectDraggedPolygonId,
    selectDraggedKeypointId,
    selectPendingPolygon,
    selectPendingKeypoints,
    selectCurrentModel,
  ],
  (
    isDrawingMode,
    isSmartSelectMode,
    draggedPolygonId,
    draggedKeypointId,
    pendingPolygon,
    pendingKeypoints,
    currentModel,
  ): boolean => {
    const keypointClassCount = currentModel?.labels.filter((label) => label.type === 'keypoint').length ?? 0;
    const hasIncompleteKeypoints =
      !!pendingKeypoints && keypointClassCount > 0 && pendingKeypoints.length < keypointClassCount;
    return (
      isDrawingMode ||
      isSmartSelectMode ||
      !!draggedPolygonId ||
      !!draggedKeypointId ||
      !!pendingPolygon ||
      hasIncompleteKeypoints
    );
  },
);

export const selectImageLabelPayloadByModel = createSelector(
  [selectAnnotationItems, selectPendingKeypoints, selectCurrentModel],
  (annotationItems, pendingKeypoints, currentModel): ImageLabelPayloadByModel => {
    const payloadByModel: ImageLabelPayloadByModel = {};

    // Process regular annotations
    Object.entries(annotationItems ?? {}).forEach(([modelId, items]) => {
      const hasChanges = items.some((item) => item.changeType !== 'none');
      if (hasChanges) {
        for (const item of items) {
          // never send machine predictions to the backend
          if (item.isMachinePrediction) {
            continue;
          }

          // create a payload for the model if it doesn't exist yet and add the item
          // we also need to create a payload if all items are deleted since we then need to send an empty array
          if (!payloadByModel[modelId]) {
            payloadByModel[modelId] = [];
          }

          // add the item to the payload if it's not deleted
          if (item.changeType !== 'deleted') {
            payloadByModel[modelId].push({
              id: item.labelId,
              coordinates: item.coordinates,
              type: item.type,
              generatedByPolygonService: item.generatedByPolygonService,
              // Only newly created polygons report a tool: an untouched or reshaped polygon that is
              // re-sent with the rest of the annotation is not a new tool use.
              annotationEventType: item.changeType === 'new' ? item.annotationEventType : undefined,
            });
          }
        }
      }
    });

    // Add keypoints from pending state
    if (pendingKeypoints) {
      pendingKeypoints.forEach(({classId, coordinates}) => {
        const modelId = currentModel?.id; // Get from current model context
        if (!modelId) return;

        if (!payloadByModel[modelId]) {
          payloadByModel[modelId] = [];
        }

        payloadByModel[modelId].push({
          id: classId,
          coordinates: [coordinates], // Wrap in array to match polygon structure
          type: 'keypoint',
          // Pending keypoints are placed in this session and discarded after the save.
          annotationEventType: 'manual',
        });
      });
    }

    return payloadByModel;
  },
);

export const selectHasUnchangedFailedSavePayload = createSelector(
  [selectImageLabelPayloadByModel, selectFailedSavePayload],
  (payloadByModel, failedSavePayload): boolean =>
    failedSavePayload !== null && isequal(failedSavePayload, payloadByModel),
);

export const selectPolygonContextMenuData = createSelector(
  [selectActivePolygons, selectPolygonContextMenu],
  (polygons, polygonContextMenu) => {
    const polygonItem = polygons.find((polygon) => polygon.id === polygonContextMenu?.polygonId);
    if (!polygonContextMenu || !polygonItem) {
      return null;
    }
    return {
      polygonItem,
      position: polygonContextMenu.position,
    };
  },
);

export const selectActivePolygon = createSelector(
  [selectActivePolygons, selectActivePolygonId],
  (polygons, activePolygonId) => {
    return polygons.find((polygon) => polygon.id === activePolygonId) ?? null;
  },
);

export const selectDeletedHumanPolygons = createSelector([selectAllAnnotations], (activeAnnotation) => {
  if (!activeAnnotation || Object.values(activeAnnotation).length === 0) {
    return [];
  }
  return Object.values(activeAnnotation).filter(
    (polygon) => !polygon.isMachinePrediction && polygon.changeType === 'deleted',
  );
});

export const selectIsKeypointMode = createSelector(select, (state) => state.isKeypointMode);

export const selectGroupedKeypointItems = createSelector(
  [selectActiveKeypoints, selectShowHumanAnnotations, selectShowMachinePredictions],
  (keypoints, showHumanAnnotations, showMachinePredictions): {[groupId: string]: KeypointItem[]} => {
    const visibleKeypoints = keypoints.filter(
      (keypoint) =>
        (keypoint.isMachinePrediction && showMachinePredictions) ||
        (!keypoint.isMachinePrediction && showHumanAnnotations),
    );

    const groupedKeypoints: {[groupId: string]: KeypointItem[]} = {};

    visibleKeypoints.forEach((keypoint) => {
      if (!keypoint.coordinates || keypoint.coordinates.length === 0) return;

      const groupId = `keypoint-${keypoint.id}`;

      groupedKeypoints[groupId] = [keypoint];
    });

    return groupedKeypoints;
  },
);

export const selectPendingKeypointItems = createSelector(
  [selectPendingKeypoints, selectCurrentModel, selectIsKeypointMode],
  (pendingKeypoints, currentModel, isKeypointMode): KeypointItem[] => {
    if (!pendingKeypoints || !currentModel || !isKeypointMode) return [];

    return pendingKeypoints.map((pendingKeypoint, index) => {
      const keypointClass = currentModel.labels.find((label) => label.id === pendingKeypoint.classId);

      return {
        id: `pending-keypoint-${index}`,
        labelId: pendingKeypoint.classId,
        color: keypointClass?.colorCode || '#ffffff',
        alias: keypointClass?.alias || '',
        modelId: currentModel.id,
        type: 'keypoint',
        isMachinePrediction: false,
        source: 'human',
        userEmail: pendingKeypoint.userEmail,
        updatedAt: Date.now() / 1000,
        changeType: 'new',
        coordinates: [pendingKeypoint.coordinates],
      };
    });
  },
);

export const selectGroupedPendingKeypointItems = createSelector(
  [selectPendingKeypointItems, selectShowHumanAnnotations],
  (pendingKeypoints, showHumanAnnotations): {[groupId: string]: KeypointItem[]} => {
    if (!showHumanAnnotations) return {};

    const groupedKeypoints: {[groupId: string]: KeypointItem[]} = {};

    pendingKeypoints.forEach((keypoint, index) => {
      const groupId = `pending-keypoint-${index}`;
      groupedKeypoints[groupId] = [keypoint];
    });

    return groupedKeypoints;
  },
);

export const selectKeypointContextMenuPosition = (state: ReduxState) => state.annotator.keypointContextMenu;

export const selectHoveredKeypoint = createSelector(select, (state) => state.hoveredKeypoint);

export const selectShowCoordinatesIndicator = createSelector(select, (state) => state.showCoordinatesIndicator);

export const selectNotification = createSelector(select, (state) => state.notification);

export const selectIsMeasuringDistance = createSelector(select, (state) => state.isMeasuringDistance);

export const selectMeasurementPoints = createSelector(select, (state) => state.measurementPoints);

export const selectMeasurementContextMenu = createSelector(select, (state) => state.measurementContextMenu);

export const selectIsLookingForCoordinates = createSelector(select, (state) => state.isLookingForCoordinates);

const isModelArchived = (models: Model[], modelId: string | undefined, currentModel?: Model) => {
  if (!modelId) return false;
  if (currentModel?.id === modelId) return false;

  const model = models.find((m) => m.id === modelId);
  return model?.archived === true;
};
